import { serviceTypeBadge } from '@/components/features/offerings/common'
import { DataTable } from '@/components/features/shared/DataTable'
import DateSelector from '@/components/features/shared/DateSelector'
import { SummaryExtended } from '@/components/features/shared/SummaryExtended'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CollectorStatus } from '@/domain/collectors/collector'
import { OfferingStatus, ServiceType } from '@/domain/offerings/offering'
import { useSalesSummaryByOffering, useSalesSummaryBySeller, useSellers } from '@/hooks/api/useSellers'
import { useOfferings } from '@/hooks/api/useOfferings'
import { formatCurrency } from '@/lib/utils'
import { useDateRange } from '@/hooks/useDateRange'
import { Paths } from '@/routes/paths'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { format, startOfMonth } from 'date-fns'
import { ArrowRight, Award, DollarSign, TrendingUp, Users } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { PageContainer } from '@/components/features/shared/PageContainer'

type ViewMode = 'sellers' | 'offerings'

const VIEW_MODE = {
  SELLERS: 'sellers' as const,
  OFFERINGS: 'offerings' as const,
} as const

interface SalesSummaryRow {
  rank: number
  sellerId: string
  sellerName: string
  sellerCode: number
  totalContracts: number
  parcelContracts: number
  cremationContracts: number
  subscriptionContracts: number
  parcelAmount: number
  cremationAmount: number
  subscriptionAmount: number
  totalAmount: number
  commissionAmount: number
  commissionPercentage: number
}

interface SalesSummaryOfferingRow {
  rank: number
  offeringId: string
  offeringCode: string
  offeringName: string
  serviceType: ServiceType
  totalContracts: number
  totalAmount: number
  totalCommissionAmount: number
}

export function SalesDashboardView() {
  const now = new Date()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, string>

  const { fromDate, toDate, setFromDate, setToDate } = useDateRange({
    fromDate: startOfMonth(now),
    toDate: now,
  })

  const [viewMode, setViewMode] = useState<ViewMode>((search.viewMode as ViewMode | undefined) || VIEW_MODE.SELLERS)
  const [selectedSellerId, setSelectedSellerId] = useState<string>(search.sellerId || 'all')
  const [selectedServiceType, setSelectedServiceType] = useState<string>(search.serviceType || 'all')
  const [selectedOfferingId, setSelectedOfferingId] = useState<string>(search.offeringId || 'all')
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })

  const setViewModeWithUrl = useCallback((v: ViewMode) => {
    setViewMode(v)
    navigate({
      search: (old: { viewMode?: ViewMode; sellerId?: string; serviceType?: string; offeringId?: string; page?: number }) => {
        const newSearch = { ...old, viewMode: v }
        if (v === VIEW_MODE.OFFERINGS) {
          newSearch.sellerId = 'all'
        } else {
          newSearch.offeringId = 'all'
        }
        return newSearch
      },
      replace: true,
    } as any)
  }, [navigate])

  const setSelectedSellerIdWithUrl = useCallback((v: string) => {
    setSelectedSellerId(v)
    navigate({
      search: (old: Record<string, string | undefined>) => ({ ...old, sellerId: v }),
      replace: true,
    } as any)
  }, [navigate])

  const setSelectedServiceTypeWithUrl = useCallback((v: string) => {
    setSelectedServiceType(v)
    navigate({
      search: (old: Record<string, string | undefined>) => ({ ...old, serviceType: v }),
      replace: true,
    } as any)
  }, [navigate])

  const setSelectedOfferingIdWithUrl = useCallback((v: string) => {
    setSelectedOfferingId(v)
    navigate({
      search: (old: Record<string, string | undefined>) => ({ ...old, offeringId: v }),
      replace: true,
    } as any)
  }, [navigate])

  const { data: allSellers = [] } = useSellers()

  const { data: allOfferings = [] } = useOfferings({
    status: OfferingStatus.ACTIVE,
    limit: 1000,
    serviceType: selectedServiceType === 'all' ? undefined : (selectedServiceType as any),
  })

  const activeSellers = useMemo(() => {
    return allSellers.filter(s => s.status === CollectorStatus.Active)
  }, [allSellers])

  const { data: salesData, isLoading, refetch } = useSalesSummaryBySeller(
    fromDate!,
    toDate!,
    selectedSellerId === 'all' ? undefined : selectedSellerId,
    selectedServiceType === 'all' ? undefined : selectedServiceType,
    { enabled: !!fromDate && !!toDate }
  )

  const { data: offeringData, isLoading: offeringLoading } = useSalesSummaryByOffering(
    fromDate!,
    toDate!,
    selectedServiceType === 'all' ? undefined : selectedServiceType,
    selectedOfferingId === 'all' ? undefined : selectedOfferingId,
    { enabled: viewMode === VIEW_MODE.OFFERINGS && !!fromDate && !!toDate }
  )

  const goToSellerSales = (sellerId: string) => {
    if (!sellerId || !fromDate || !toDate) return
    navigate({
      to: `${Paths.Management.SellerSales}`,
      params: { sellerId },
      search: { dateFrom: format(fromDate, 'yyyy-MM-dd'), dateTo: format(toDate, 'yyyy-MM-dd') },
    })
  }

  const goToOfferingContracts = (offeringId: string) => {
    if (!offeringId || !fromDate || !toDate) return
    navigate({
      to: `${Paths.Management.OfferingSales}`,
      params: { offeringId },
      search: { dateFrom: format(fromDate, 'yyyy-MM-dd'), dateTo: format(toDate, 'yyyy-MM-dd') },
    })
  }

  const rows: SalesSummaryRow[] = useMemo(() => {
    if (!salesData?.salesSummaryBySeller) return []
    return salesData.salesSummaryBySeller.map((seller, index) => ({
      rank: index + 1,
      sellerId: seller.sellerId,
      sellerName: seller.sellerName,
      sellerCode: seller.sellerCode,
      totalContracts: seller.totalContracts,
      parcelContracts: seller.parcelContracts,
      cremationContracts: seller.cremationContracts,
      subscriptionContracts: seller.subscriptionContracts,
      parcelAmount: seller.parcelAmount,
      cremationAmount: seller.cremationAmount,
      subscriptionAmount: seller.subscriptionAmount,
      totalAmount: seller.totalAmount,
      commissionAmount: seller.commissionAmount,
      commissionPercentage: seller.commissionPercentage ?? 0,
    }))
  }, [salesData])

  const offeringRows: SalesSummaryOfferingRow[] = useMemo(() => {
    if (!offeringData) return []
    return offeringData.map((off, index) => ({
      rank: index + 1,
      offeringId: off.offeringId,
      offeringCode: off.offeringCode,
      offeringName: off.offeringName,
      serviceType: off.serviceType,
      totalContracts: off.totalContracts,
      totalAmount: off.totalAmount,
      totalCommissionAmount: off.totalCommissionAmount,
    }))
  }, [offeringData])

  const offeringTotalStats = useMemo(() => {
    if (!offeringData || offeringData.length === 0) {
      return {
        totalContracts: 0,
        totalAmount: 0,
        totalCommissionAmount: 0,
      }
    }
    return offeringData.reduce(
      (acc, off) => ({
        totalContracts: acc.totalContracts + off.totalContracts,
        totalAmount: acc.totalAmount + off.totalAmount,
        totalCommissionAmount: acc.totalCommissionAmount + off.totalCommissionAmount,
      }),
      { totalContracts: 0, totalAmount: 0, totalCommissionAmount: 0 }
    )
  }, [offeringData])

  const sellerTotalStats = salesData?.totalStats || {
    totalContracts: 0,
    parcelContracts: 0,
    cremationContracts: 0,
    subscriptionContracts: 0,
    parcelAmount: 0,
    cremationAmount: 0,
    subscriptionAmount: 0,
    totalAmount: 0,
    totalCommissionAmount: 0,
  }

  const totalStats = viewMode === VIEW_MODE.OFFERINGS ? offeringTotalStats : sellerTotalStats

  const columns: ColumnDef<SalesSummaryRow>[] = [
    {
      accessorKey: 'rank',
      header: 'Nro',
      cell: ({ row }) => {
        const rank = row.original.rank
        // if (rank === 1) return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 font-bold">1</Badge>
        // if (rank === 2) return <Badge variant="outline" className="bg-gray-100 text-gray-700 font-bold">2</Badge>
        // if (rank === 3) return <Badge variant="outline" className="bg-amber-100 text-amber-700 font-bold">3</Badge>
        return <span className="font-medium text-muted-foreground">{rank}</span>
      },
    },
    {
      accessorKey: 'sellerCode',
      header: 'Codigo',
      cell: ({ row }) => {
        const code = row.original.sellerCode
        if (!code) return <span>-</span>
        return (
          <Badge variant="outline" className="bg-gray-50 font-mono">
            {code}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'sellerName',
      header: 'Vendedor',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.sellerName || 'Sin Vendedor'}</div>
      ),
    },
    {
      id: 'contracts',
      header: 'Contratos',
      cell: ({ row }) => (
        <div className="flex items-center gap-3 text-sm">
          <span className="font-medium">{row.original.totalContracts}</span>
          <span className="text-muted-foreground text-xs">
            ({row.original.parcelContracts}P / {row.original.cremationContracts}C / {row.original.subscriptionContracts}A)
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'parcelAmount',
      header: 'Parcelas',
      cell: ({ row }) => (
        <div className="text-sm font-mono">{formatCurrency(row.original.parcelAmount)}</div>
      ),
    },
    {
      accessorKey: 'cremationAmount',
      header: 'Cremaciones',
      cell: ({ row }) => (
        <div className="text-sm font-mono">{formatCurrency(row.original.cremationAmount)}</div>
      ),
    },
    {
      accessorKey: 'subscriptionAmount',
      header: 'Abonos',
      cell: ({ row }) => (
        <div className="text-sm font-mono">{formatCurrency(row.original.subscriptionAmount)}</div>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Vendido',
      cell: ({ row }) => (
        <div className="text-sm font-mono font-bold">{formatCurrency(row.original.totalAmount)}</div>
      ),
    },
    {
      accessorKey: 'commissionPercentage',
      header: '% Comision',
      cell: ({ row }) => (
        <div className="text-sm font-mono font-medium">
          {(row.original.commissionPercentage ?? 0).toFixed(1)}%
        </div>
      ),
    },
    {
      accessorKey: 'commissionAmount',
      header: 'Comision',
      cell: ({ row }) => (
        <div className="text-sm font-mono text-emerald-600 font-medium">
          {formatCurrency(row.original.commissionAmount)}
        </div>
      ),
    },
    {
      id: 'acciones',
      header: 'Acciones',
      cell: ({ row }) => (
        <Button
          variant={"outline"}
          size="sm"
          title='Ver ventas'
          onClick={(e) => {
            e.stopPropagation()
            goToSellerSales(row.original.sellerId)
          }}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      ),
    },
  ]

  const offeringColumns: ColumnDef<SalesSummaryOfferingRow>[] = [
    {
      accessorKey: 'rank',
      header: 'Nro',
      cell: ({ row }) => {
        const rank = row.original.rank
        // if (rank === 1) return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 font-bold">1</Badge>
        // if (rank === 2) return <Badge variant="outline" className="bg-gray-100 text-gray-700 font-bold">2</Badge>
        // if (rank === 3) return <Badge variant="outline" className="bg-amber-100 text-amber-700 font-bold">3</Badge>
        return <span className="font-medium text-muted-foreground">{rank}</span>
      },
    },
    {
      accessorKey: 'offeringCode',
      header: 'Codigo',
      cell: ({ row }) => (
        <Badge variant="outline" className="bg-gray-50 font-mono">
          {row.original.offeringCode}
        </Badge>
      ),
    },
    {
      accessorKey: 'offeringName',
      header: 'Servicio',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.offeringName}</div>
      ),
    },
    {
      accessorKey: 'serviceType',
      header: 'Tipo',
      cell: ({ row }) => (
        serviceTypeBadge(row.original.serviceType)
      ),
    },
    {
      id: 'contracts',
      header: 'Contratos',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.totalContracts}</span>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Vendido',
      cell: ({ row }) => (
        <div className="text-sm font-mono font-bold">{formatCurrency(row.original.totalAmount)}</div>
      ),
    },
    {
      accessorKey: 'totalCommissionAmount',
      header: 'Comision',
      cell: ({ row }) => (
        <div className="text-sm font-mono text-emerald-600 font-medium">
          {formatCurrency(row.original.totalCommissionAmount)}
        </div>
      ),
    },
    {
      id: 'acciones',
      header: 'Acciones',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          title='Ver ventas'
          onClick={(e) => {
            e.stopPropagation()
            goToOfferingContracts(row.original.offeringId)
          }}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      ),
    },
  ]

  return (
    <PageContainer>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Panel de Ventas</h1>
          <p className="text-muted-foreground">
            Resumen de ventas y rendimiento {viewMode === VIEW_MODE.SELLERS ? 'de vendedores' : 'por servicio'}
          </p>
        </div>
        <SegmentedControl value={viewMode} onValueChange={setViewModeWithUrl}>
          <SegmentedControlItem value="sellers">
            <Users className="h-4 w-4" />
            Vendedores
          </SegmentedControlItem>
          <SegmentedControlItem value="offerings">
            <DollarSign className="h-4 w-4" />
            Ventas
          </SegmentedControlItem>
        </SegmentedControl>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-5 flex-wrap">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Desde</Label>
              <DateSelector initialDate={fromDate} onDateSelected={setFromDate} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Hasta</Label>
              <DateSelector initialDate={toDate} onDateSelected={setToDate} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="serviceTypeFilter" className="text-xs">Tipo de Servicio</Label>
              <Select value={selectedServiceType} onValueChange={setSelectedServiceTypeWithUrl}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Todos los servicios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los servicios</SelectItem>
                  <SelectItem value="parcelas">Parcelas</SelectItem>
                  <SelectItem value="cremaciones">Cremaciones</SelectItem>
                  <SelectItem value="abonos">Abonos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {viewMode === VIEW_MODE.SELLERS && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="sellerFilter" className="text-xs">Vendedor</Label>
                <Select value={selectedSellerId} onValueChange={setSelectedSellerIdWithUrl}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Todos los vendedores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los vendedores</SelectItem>
                    {activeSellers.map((seller) => (
                      <SelectItem key={seller.id} value={seller.id}>
                        {seller.name} ({seller.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {viewMode === VIEW_MODE.OFFERINGS && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="offeringFilter" className="text-xs">Servicio</Label>
                <Select value={selectedOfferingId} onValueChange={setSelectedOfferingIdWithUrl}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Todos los servicios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los servicios</SelectItem>
                    {allOfferings.map((offering) => (
                      <SelectItem key={offering.id} value={offering.id}>
                        {offering.name} ({offering.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-col ml-auto gap-1">
              <Label className="text-xs">&nbsp;</Label>
              <Button variant={"info"} onClick={() => refetch()}>
                Buscar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <SummaryExtended
        items={[
          {
            title: 'Total Vendido',
            value: formatCurrency(totalStats.totalAmount),
            icon: <DollarSign className="h-4 w-4 text-muted-foreground" />,
            subtitle: fromDate && toDate ? `${format(fromDate, 'dd/MM/yyyy')} al ${format(toDate, 'dd/MM/yyyy')}` : '',
          },
          {
            title: 'Contratos vendidos',
            value: totalStats.totalContracts,
            icon: <TrendingUp className="h-4 w-4 text-blue-600" />,
            subtitle: viewMode === VIEW_MODE.SELLERS
              ? `${sellerTotalStats.parcelContracts} Parcelas / ${sellerTotalStats.cremationContracts} Cremaciones / ${sellerTotalStats.subscriptionContracts} Abonos`
              : '',
          },
          {
            title: 'Comisiones',
            value: formatCurrency(totalStats.totalCommissionAmount),
            icon: <Award className="h-4 w-4 text-emerald-600" />,
            subtitle: 'Total a pagar',
            valueClassName: 'text-emerald-600',
          },
          {
            title: 'Vendedores Activos',
            value: activeSellers.length,
            icon: <Users className="h-4 w-4 text-muted-foreground" />,
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>{viewMode === VIEW_MODE.SELLERS ? 'Ranking de Vendedores' : 'Ranking por Servicio'}</CardTitle>
        </CardHeader>
        <CardContent>
          {viewMode === VIEW_MODE.SELLERS && isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Cargando datos...</p>
            </div>
          ) : viewMode === VIEW_MODE.OFFERINGS && offeringLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Cargando datos...</p>
            </div>
          ) : (
            <>
              {viewMode === VIEW_MODE.SELLERS && (
                <DataTable
                  columns={columns}
                  data={rows}
                  totalRecords={rows.length}
                  pagination={pagination}
                  onPaginationChange={setPagination}
                  onRowClick={(row: SalesSummaryRow) => goToSellerSales(row.sellerId)}
                  searchPlaceholder="Buscar vendedor..."
                  searchKey="sellerName"
                />
              )}
              {viewMode === VIEW_MODE.OFFERINGS && (
                <DataTable
                  columns={offeringColumns}
                  data={offeringRows}
                  totalRecords={offeringRows.length}
                  pagination={pagination}
                  onPaginationChange={setPagination}
                  searchPlaceholder="Buscar servicio..."
                  searchKey="offeringName"
                  onRowClick={(row: SalesSummaryOfferingRow) => goToOfferingContracts(row.offeringId)}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
